import { GridSortModel } from '@mui/x-data-grid';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import { format, parseISO } from 'date-fns';

import { Bot, BotLabel, ConversationHistory, NewBot } from '@/domain/bot';
import {
    BotMappie,
    BotMappieNewVersion,
    BotMappieVersion,
} from '@/domain/bot-mappie';
import { IdsAndPermissions } from '@/domain/call-details';
import { HistoryFilter } from '@/domain/data-grid-filter';

const BOTS_API = '/api/bots';

export const getBots = async (accountId?: string | null) => {
    try {
        const response = await axios.get<BotMappie[]>(BOTS_API, {
            params: { account_id: accountId },
        });
        return response.data;
    } catch (error: any) {
        throw error;
    }
};

export const getBot = async (botId: string) => {
    const response = await axios.get<Bot>(`/${botId}`, {
        baseURL: BOTS_API,
    });
    return response.data;
};

export const getBotRaw = async (botId: string, accountId?: string | null) => {
    const response = await axios.get<BotMappie>(`/${botId}/raw`, {
        baseURL: BOTS_API,
        params: {
            account_id: accountId,
        },
    });
    return response.data;
};

export const postBot = async (newBot: NewBot, accountId: string | null) => {
    const response = await axios.post<NewBot, AxiosResponse<BotMappie>>(
        BOTS_API,
        newBot,
        {
            params: {
                account_id: accountId,
            },
        },
    );
    return response.data;
};

export const postBotRaw = async (
    newBot: BotMappie,
    accountId?: string | null,
) => {
    const response = await axios.post<BotMappie, AxiosResponse<BotMappie>>(
        '/raw',
        newBot,
        {
            baseURL: BOTS_API,
            params: {
                account_id: accountId,
            },
        },
    );
    return response.data;
};

export const deleteBot = async (botId: string, accountId?: string | null) => {
    await axios.delete<null>(`/${botId}`, {
        baseURL: BOTS_API,
        params: {
            account_id: accountId,
        },
    });
    return;
};

export const getBotVersions = async (botId: string) => {
    const response = await axios.get<BotMappieVersion[]>(
        `/${botId}/bot_versions`,
        {
            baseURL: BOTS_API,
        },
    );
    return response.data;
};

export const getBotVersion = async (
    botId: string,
    botVersionId: string,
    accountId?: string,
) => {
    const response = await axios.get<BotMappieVersion>(
        `/${botId}/bot_versions/${botVersionId}`,
        {
            baseURL: BOTS_API,
            params: {
                account_id: accountId,
            },
        },
    );
    return response.data;
};

export const postBotVersion = async (
    botId: string,
    botNewVersion: BotMappieNewVersion,
) => {
    const response = await axios.post<
        BotMappieNewVersion,
        AxiosResponse<{ id: string }>
    >(`/${botId}/bot_versions`, botNewVersion, {
        baseURL: BOTS_API,
    });
    return response.data;
};

export const patchBotVersion = async (
    botId: string,
    botVersionId: string,
    updatedBotVersion: BotMappieNewVersion,
    accountId?: string,
) => {
    await axios.patch<BotMappieVersion>(
        `/${botId}/bot_versions/${botVersionId}`,
        updatedBotVersion,
        {
            baseURL: BOTS_API,
            params: {
                account_id: accountId,
            },
        },
    );
    return;
};

export const getBotCallHistory = async (
    idsAndPermissions: IdsAndPermissions,
    paginationModel: {
        page: number;
        pageSize: number;
    },
    filters: HistoryFilter,
    sortModel?: GridSortModel,
) => {
    const response = await axios.get<ConversationHistory>(
        `/${idsAndPermissions.botId}/call_history`,
        {
            baseURL: BOTS_API,
            params: {
                account_id: idsAndPermissions.accountId,
                is_chat: idsAndPermissions.isChat,
                is_kpi: idsAndPermissions.isKPICall,
                is_old_elastic: idsAndPermissions.isOldElastic,
                page: paginationModel.page,
                page_size: paginationModel.pageSize,
                filters: JSON.stringify(filters),
                sortModel: sortModel ? JSON.stringify(sortModel) : undefined,
            },
        },
    );
    return response.data;
};

export const getBotStatsForDashboard = async (
    accountId: string,
    botId: string,
    { startDate, endDate }: { startDate: string; endDate: string },
    showAllBots: boolean,
) => {
    try {
        // Format dates to ISO string for Elasticsearch
        const formattedStartDate = format(parseISO(startDate), 'yyyy-MM-dd');
        const formattedEndDate = format(parseISO(endDate), 'yyyy-MM-dd');

        const response = await axios.get(`/api/bots/${botId}/stats`, {
            params: {
                show_all_bots: showAllBots,
                account_id: accountId,
                start_date: formattedStartDate,
                end_date: formattedEndDate,
            },
        });

        return response.data;
    } catch (error) {
        console.error('Error fetching bot stats:', error);
        throw error;
    }
};

export const getBotChatHistory = async (botId: string, accountId?: string) => {
    const response = await axios.get<ConversationHistory>(
        `/${botId}/chat_history`,
        {
            baseURL: BOTS_API,
            params: {
                account_id: accountId,
            },
        },
    );
    return response.data;
};

export const getAvailableBotLabels = async (accountId?: string | null) => {
    const response = await axios.get<BotLabel[]>(`/labels`, {
        baseURL: BOTS_API,
        params: {
            account_id: accountId,
        },
    });
    return response.data;
};

export const patchBotLabels = async (
    botId: string,
    labels: string[],
    accountId?: string | null,
) => {
    await axios.patch(`/${botId}/labels`, labels, {
        baseURL: BOTS_API,
        params: {
            account_id: accountId,
        },
    });
    return;
};

export const publishBot = async (
    machrBotId: string,
    backendMachrBotId: string,
    botId: string,
    botVersionId: string,
    accountId?: string | null,
) => {
    await axios.post(
        `/${botId}/bot_versions/${botVersionId}/publish?machr_bot_id=${machrBotId}&backendMachrBotId=${backendMachrBotId}`,
        {},
        {
            baseURL: BOTS_API,
            params: {
                account_id: accountId,
            },
        },
    );
};

export const saveNewBotVersion = async (
    botId: string,
    botVersionId: string,
    updatedBotVersion: BotMappieNewVersion,
    accountId?: string,
) => {
    await axios.post<BotMappieVersion>(
        `/${botId}/bot_versions/${botVersionId}/save-new-version`,
        updatedBotVersion,
        {
            baseURL: BOTS_API,
            params: {
                account_id: accountId,
            },
        },
    );
    return;
};
